import { useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";

import {
	DeleteUpdateCustomDataRequest,
	addCustomData,
	deleteUpdateCustomData,
	getCustomDatas,
} from "../../api";
import { CustomDataDto } from "../../types/dto";
import { CustomDataTargetType } from "../../types/dto";
import { BlockInput } from "../blockInput";
import { CustomData } from "./customData";
import { CustomDataModal } from "./customDataModal";

export const CustomDataList = ({
	id,
	type,
}: {
	id: string;
	type: CustomDataTargetType;
}) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [selectedCustomData, setSelectedCustomData] = useState(
		{} as CustomDataDto | {}
	);

	const { data, isLoading, isFetching, error } = useQuery(
		[Object.keys({ getCustomDatas })[0], id],
		() =>
			getCustomDatas({
				targetId: id,
				targetType: type,
			})
	);

	const customDatas = data?.customDatas || [];

	const queryClient = useQueryClient();
	const refreshCustomData = () =>
		queryClient.invalidateQueries({
			queryKey: [Object.keys({ getCustomDatas })[0], id],
		});

	const { mutateAsync: submitEditCustomData, isLoading: isLoadingEdit } =
		useMutation({
			mutationFn: (cd: CustomDataDto) => {
				const req = {
					...cd,
					customDataId: cd.id,
				} as DeleteUpdateCustomDataRequest;
				return deleteUpdateCustomData(req, false);
			},
			onSettled: refreshCustomData,
		});

	const { mutateAsync: submitDeleteCustomData, isLoading: isLoadingDelete } =
		useMutation({
			mutationFn: (cd: CustomDataDto) => {
				const req = {
					...cd,
					customDataId: cd.id,
				} as DeleteUpdateCustomDataRequest;
				return deleteUpdateCustomData(req, true);
			},
			onSettled: refreshCustomData,
		});

	const { mutateAsync: switchActive } = useMutation({
		mutationFn: (req: DeleteUpdateCustomDataRequest) =>
			deleteUpdateCustomData(req, false),
		onSettled: refreshCustomData,
	});

	const { mutateAsync: submitAddCustomData } = useMutation({
		mutationFn: () =>
			addCustomData({
				targetId: id,
				targetType: type,
			}),
		onSettled: refreshCustomData,
	});

	const customDataList = useMemo(() => {
		const len = customDatas.length ?? 0;
		if (len < 1) return "No Custom Data";
		return customDatas
			.map((cd: CustomDataDto, index: number) => (
				<CustomData
					key={index}
					customData={cd}
					openEditModal={() => {
						setSelectedCustomData(cd);
						setIsEditModalOpen(true);
					}}
					openDeleteModal={() => {
						setSelectedCustomData(cd);
					}}
					onSwitchChanged={(checked) =>
						switchActive({
							customDataId: cd.id,
							isEnabled: checked,
						})
					}
				/>
			))
			.reverse();
	}, [customDatas, isFetching]);

	return (
		<>
			<CustomDataModal
				key={(selectedCustomData as CustomDataDto).id}
				initialCustomData={selectedCustomData}
				isModalOpen={isEditModalOpen}
				setIsModalOpen={setIsEditModalOpen}
				submitEditCustomData={submitEditCustomData}
				submitDeleteCustomData={submitDeleteCustomData}
			/>
			<Card border="light">
				<BlockInput
					blocking={
						isLoadingDelete ||
						isLoading ||
						isLoadingEdit ||
						isFetching
					}
				/>
				<Card.Header>
					Custom Data
					<Button
						onClick={() => submitAddCustomData()}
						className="float-end"
						variant="primary"
						size="sm"
					>
						Add Custom Data
					</Button>
				</Card.Header>
				<Card.Body>
					{error ? "Something's gone wrong" : customDataList}
				</Card.Body>
			</Card>
		</>
	);
};
